import { and, asc, desc, eq, isNull } from 'drizzle-orm';
import { history, shoppingItems, shoppingTripItems, shoppingTrips } from '@tahi/db';
import {
  pricePoints,
  type PricePoint,
  type ShoppingItemLike,
  type ShoppingTripItemLike,
  type ShoppingTripLike,
} from '@tahi/core';
import type { LocalDb } from '../localDb';

export interface ShoppingItem extends ShoppingItemLike {
  createdAt: string;
}

export interface ShoppingTrip extends ShoppingTripLike {
  createdAt: string;
}

export interface ShoppingTripLine extends ShoppingTripItemLike {
  createdAt: string;
  deletedAt: string | null;
}

/** A trip line with its trip's date (for suggestions). */
export interface DatedLine extends ShoppingTripLine {
  date: string;
}

export interface PriceData {
  /** Live lines of live trips, any price. */
  lines: DatedLine[];
  /** Lines with a price, oldest first. */
  points: PricePoint[];
}

export interface TripHistoryRow {
  id: string;
  action: string;
  entityType: string;
  at: string;
}

type ItemRow = typeof shoppingItems.$inferSelect;
type TripRow = typeof shoppingTrips.$inferSelect;
type LineRow = typeof shoppingTripItems.$inferSelect;

function toItem(r: ItemRow): ShoppingItem {
  return {
    id: r.id,
    name: r.name,
    quantity: r.quantity ?? null,
    unit: r.unit ?? null,
    estimateMinor: r.estimateMinor ?? null,
    store: r.store ?? null,
    position: r.position ?? 0,
    tripId: r.tripId ?? null,
    deletedAt: r.deletedAt ?? null,
    createdAt: r.createdAt,
  };
}

function toTrip(r: TripRow): ShoppingTrip {
  return {
    id: r.id,
    date: r.date,
    store: r.store ?? null,
    totalMinor: r.totalMinor,
    expenseId: r.expenseId ?? null,
    deletedAt: r.deletedAt ?? null,
    createdAt: r.createdAt,
  };
}

function toLine(r: LineRow): ShoppingTripLine {
  return {
    id: r.id,
    tripId: r.tripId,
    itemId: r.itemId ?? null,
    name: r.name,
    normalizedName: r.normalizedName,
    quantity: r.quantity ?? null,
    unit: r.unit ?? null,
    priceMinor: r.priceMinor ?? null,
    position: r.position ?? 0,
    createdAt: r.createdAt,
    deletedAt: r.deletedAt ?? null,
  };
}

/** The open list (not bought, not removed), in the user's order. */
export async function listOpenItems(db: LocalDb, userId: string): Promise<ShoppingItem[]> {
  const rows = await db
    .select()
    .from(shoppingItems)
    .where(
      and(
        eq(shoppingItems.userId, userId),
        isNull(shoppingItems.deletedAt),
        isNull(shoppingItems.tripId),
      ),
    )
    .orderBy(asc(shoppingItems.position), asc(shoppingItems.createdAt));
  return rows.map(toItem);
}

/** One item, including a bought or removed one. */
export async function getItem(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<ShoppingItem | null> {
  const rows = await db
    .select()
    .from(shoppingItems)
    .where(and(eq(shoppingItems.userId, userId), eq(shoppingItems.id, id)))
    .limit(1);
  return rows[0] ? toItem(rows[0]) : null;
}

export async function getItems(
  db: LocalDb,
  userId: string,
  ids: readonly string[],
): Promise<ShoppingItem[]> {
  const out: ShoppingItem[] = [];
  for (const id of ids) {
    const item = await getItem(db, userId, id);
    if (item) out.push(item);
  }
  return out;
}

/** Live trips, newest first. */
export async function listTrips(db: LocalDb, userId: string): Promise<ShoppingTrip[]> {
  const rows = await db
    .select()
    .from(shoppingTrips)
    .where(and(eq(shoppingTrips.userId, userId), isNull(shoppingTrips.deletedAt)))
    .orderBy(desc(shoppingTrips.date), desc(shoppingTrips.createdAt));
  return rows.map(toTrip);
}

/** One trip, including a deleted one (its page offers Restore). */
export async function getTrip(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<ShoppingTrip | null> {
  const rows = await db
    .select()
    .from(shoppingTrips)
    .where(and(eq(shoppingTrips.userId, userId), eq(shoppingTrips.id, id)))
    .limit(1);
  return rows[0] ? toTrip(rows[0]) : null;
}

/**
 * A trip's lines in order. Lines are deleted together with their trip, so a deleted trip's
 * lines are returned too (for its page and for Restore).
 */
export async function listTripLines(
  db: LocalDb,
  userId: string,
  tripId: string,
): Promise<ShoppingTripLine[]> {
  const rows = await db
    .select()
    .from(shoppingTripItems)
    .where(and(eq(shoppingTripItems.userId, userId), eq(shoppingTripItems.tripId, tripId)))
    .orderBy(asc(shoppingTripItems.position));
  return rows.map(toLine);
}

/** Every live line of every live trip, plus the price points among them. */
export async function loadPriceData(db: LocalDb, userId: string): Promise<PriceData> {
  const trips = await db
    .select()
    .from(shoppingTrips)
    .where(and(eq(shoppingTrips.userId, userId), isNull(shoppingTrips.deletedAt)));
  const byId = new Map(
    trips.map((t) => [t.id, { date: t.date, store: t.store ?? null, deletedAt: null }]),
  );
  const rows = await db
    .select()
    .from(shoppingTripItems)
    .where(and(eq(shoppingTripItems.userId, userId), isNull(shoppingTripItems.deletedAt)));
  const lines: DatedLine[] = [];
  for (const r of rows) {
    const trip = byId.get(r.tripId);
    if (trip) lines.push({ ...toLine(r), date: trip.date });
  }
  return { lines, points: pricePoints(lines, byId) };
}

/** History rows for a trip itself, oldest first (bought, deleted, restored). */
export async function listTripHistory(
  db: LocalDb,
  userId: string,
  tripId: string,
): Promise<TripHistoryRow[]> {
  const rows = await db
    .select()
    .from(history)
    .where(
      and(
        eq(history.userId, userId),
        eq(history.entityType, 'shopping_trips'),
        eq(history.entityId, tripId),
      ),
    )
    .orderBy(asc(history.at));
  return rows.map((r) => ({ id: r.id, action: r.action, entityType: r.entityType, at: r.at }));
}
